import { Link } from "react-router-dom";
import { ArrowRight, Clock, Heart, HeartPulse, Salad, Dumbbell, ShieldPlus, CalendarCheck } from "lucide-react";
import { BloodDropIcon, LdlDropIcon } from "@/components/icons/MedicalIcons";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookingForm from "@/components/BookingForm";
import { HOME } from "@/constants/testIds";

const HERO_IMG = "/img/hero.png";

const services = [
  {
    slug: "diabetes",
    name: "Diabetes",
    icon: BloodDropIcon,
    blurb: "Personalised treatment for type 1, type 2 and prediabetes.",
  },
  {
    slug: "hypertension",
    name: "Hypertension",
    icon: Heart,
    blurb: "Bring your numbers down with lifestyle + the right medicines.",
  },
  {
    slug: "dyslipidemia",
    name: "High Cholesterol Levels",
    icon: LdlDropIcon,
    blurb: "Cholesterol and triglyceride management to protect your heart and brain.",
  },
  {
    slug: "prevention",
    name: "Prevention",
    icon: ShieldPlus,
    blurb:
      "We use specialised calculators to assess your risk of stroke or heart attack — and start the necessary measures even before it happens.",
    highlight: true,
  },
];

const pillars = [
  { icon: Salad, title: "Diet", text: "Practical, culturally familiar meal plans you'll actually follow." },
  { icon: Dumbbell, title: "Exercise", text: "Sustainable movement built around your body and your day." },
  { icon: HeartPulse, title: "Medicine", text: "Evidence based — only the required medications and tests." },
  { icon: CalendarCheck, title: "Regular follow up", text: "Scheduled reviews so your plan keeps pace with your progress." },
];

export default function Home() {
  return (
    <main>
      {/* HERO */}
      <section data-testid={HOME.hero} className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-8 md:pb-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-in text-center lg:text-left">
            <div
              data-testid={HOME.uspBadge}
              className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))]/15 border border-[hsl(var(--primary))]/40 text-slate-800 px-4 py-1.5 text-xs font-medium tracking-wide uppercase"
            >
              <Clock size={14} /> 20-min first consultation · Diabetes · Hypertension · Cholesterol
            </div>
            <h1 className="font-serif-display mt-6 text-4xl sm:text-5xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900 font-semibold whitespace-nowrap">
              <span className="block sm:inline">Prevent</span>
              <span className="hidden sm:inline"> — </span>
              <span className="block sm:inline">Treat — Heal</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Comprehensive care and prevention for diabetes, hypertension and
              high cholesterol — with risk assessment for stroke and heart
              attack — combining detailed history taking, the right medication,
              mindful nutrition and movement that fits your life.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center lg:justify-start gap-4">
              <a href="#book">
                <Button
                  data-testid={HOME.heroCta}
                  className="rounded-full h-12 px-8 bg-[hsl(var(--primary-bold))] text-slate-900 font-semibold hover:bg-[hsl(var(--primary-bold))]/90 shadow-sm"
                >
                  Book Appointment <ArrowRight size={18} className="ml-2" />
                </Button>
              </a>
              <Link to="/know-your-disease">
                <Button
                  variant="outline"
                  className="rounded-full h-12 px-8 border-slate-300 text-slate-800 hover:bg-slate-50"
                >
                  Know your disease
                </Button>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 max-w-2xl gap-6">
              {pillars.map((p) => (
                <div key={p.title} className="flex gap-3 items-start">
                  <div className="rounded-xl bg-emerald-50 p-2.5 text-slate-800">
                    <p.icon size={18} />
                  </div>
                  <div>
                    <div className="font-medium text-slate-900 text-sm">{p.title}</div>
                    <div className="text-xs text-slate-500 leading-snug">{p.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-in">
            <div className="relative rounded-[2rem] overflow-hidden border border-slate-200 shadow-xl shadow-slate-200/60 aspect-[5/4]">
              <img
                src={HERO_IMG}
                alt="Doctor consulting a patient at Namya Health"
                className="w-full h-full object-cover"
                style={{ objectPosition: "70% 50%" }}
              />
              <div className="absolute bottom-5 left-5 right-5 bg-white/90 backdrop-blur rounded-2xl p-4 flex items-center gap-3 shadow">
                <div className="rounded-xl bg-[hsl(var(--primary))]/30 p-2">
                  <Clock size={20} className="text-slate-900" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Our USP</div>
                  <div className="text-sm font-semibold text-slate-900">
                    20-minute first consultation for diabetes, hypertension or high cholesterol
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES WE TREAT */}
      <section
        data-testid={HOME.servicesSection}
        className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-20 md:pt-12 md:pb-28"
      >
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-slate-500">What we treat</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 text-slate-900 leading-tight">
              Four pillars. One integrated approach.
            </h2>
          </div>
          <Link to="/know-your-disease" className="text-sm font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
            Read the blog <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {services.map((s) => {
            const Icon = s.icon;
            const highlighted = s.highlight;
            return (
              <div
                key={s.slug}
                data-testid={HOME.serviceCard(s.slug)}
                className="h-full"
              >
                <Card
                  className={
                    highlighted
                      ? "rounded-3xl border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 shadow-sm ring-1 ring-[hsl(var(--primary))]/20 h-full"
                      : "rounded-3xl border-slate-200 h-full"
                  }
                >
                  <CardContent className="p-8">
                    <div
                      className={
                        highlighted
                          ? "w-12 h-12 rounded-2xl bg-[hsl(var(--primary-bold))]/35 grid place-items-center text-slate-900 mb-6"
                          : "w-12 h-12 rounded-2xl bg-[hsl(var(--primary-bold))]/20 grid place-items-center text-slate-900 mb-6"
                      }
                    >
                      <Icon size={22} />
                    </div>
                    <div className="font-serif-display text-3xl text-slate-900">{s.name}</div>
                    <p className="text-slate-600 mt-3 leading-relaxed">{s.blurb}</p>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </section>

      {/* ABOUT */}
      <section
        data-testid={HOME.aboutSection}
        className="bg-emerald-50/60 border-y border-slate-200/70"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="text-xs tracking-[0.2em] uppercase text-slate-500">About</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 text-slate-900 leading-tight">
              Unhurried, honest, integrated care.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-slate-700 text-lg leading-relaxed">
            <p>
              Namya Health was started with one simple belief — chronic conditions deserve
              chronic attention, not a five-minute prescription. We treat diabetes,
              hypertension and high cholesterol levels together with diet, exercise and the right
              medication, because that's how the body actually heals.
            </p>
            <p>
              Just as importantly, we focus on <span className="font-semibold text-slate-900">prevention</span>.
              Using validated risk calculators we assess your chances of a stroke or
              heart attack — and put the right measures in place well before either
              can happen.
            </p>
            <p>
              Every new patient gets a <span className="font-semibold text-slate-900">full 20 minutes</span> for
              their first visit. We listen, we read your reports carefully, and we build a
              plan you can stick to.
            </p>
          </div>
        </div>
      </section>

      {/* BOOKING */}
      <section
        id="book"
        data-testid={HOME.bookingSection}
        className="bg-slate-900 text-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.2em] uppercase text-slate-400">Visit Us</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
              Start with a thorough first visit.
            </h2>
            <p className="text-slate-300 mt-5 text-lg leading-relaxed">
              Pick a slot that's still open and tell us a little about yourself.
              Your booking is reserved instantly — no callback needed.
            </p>
            <div className="mt-10 rounded-2xl bg-slate-800/60 border border-slate-700 p-6">
              <div className="text-sm tracking-widest uppercase text-slate-400">Clinic hours</div>
              <div className="font-serif-display text-2xl mt-1">5:30 PM – 6:30 PM</div>
              <div className="text-xs text-slate-400 mt-1">Sunday holiday · Walk-in & appointments welcome</div>
              <div className="text-sm text-slate-300 mt-4">
                Namya Clinic, Shop No. 3,<br />
                Sahasrarjuna Circle, Navanagar,<br />
                Hubli – 580025.
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <BookingForm />
          </div>
        </div>
      </section>
    </main>
  );
}
